import { useId, useMemo } from 'react';
import { Field, Flex, HStack, Input, Menu, Portal, Text } from '@chakra-ui/react';

/**
 * @useFor the **toolbar** above a `DataTable` — search input, filter
 * pills (`ToggleGroup`), add/export buttons, and any other per-view
 * controls. The canonical composition pattern for list pages.
 *
 * Examples in-tree:
 *   - People > Users (search + status filter + Add User)
 *   - Sites > Protocols (search + urgency filter + Add Protocol)
 *   - Reunify > Plans (search + status filter + Add Plan)
 *
 * **Do NOT use** for:
 *   - **3rd-tier sub-nav** (Site protocols / District defaults) — use
 *     `TabMenu`. TableOptions sits *under* a TabMenu when both exist.
 *   - **Drawer / dialog toolbars** — those have their own internal
 *     header pattern (`DrawerFormFooter` + a body header).
 *   - **Standalone filter rows** with no associated table — pull the
 *     pieces you need (`ToggleGroup`, `searchInput`) directly.
 *
 * See `libs/themes/aegix/CONVENTIONS.md` for the full pattern map.
 */
import {
  HiMagnifyingGlass,
  HiFunnel,
  HiPlus,
} from 'react-icons/hi2';
import { Button } from '../button';
// `ToggleGroup` is the canonical segmented control (shared with the
// per-protocol drawer); its config types are re-exported below so
// TableOptions consumers keep a single import site for them.
import { ToggleGroup, type ToggleOption, type ToggleGroupConfig } from './ToggleGroup';
// `ExportMenuItem` is declared once, next to the `ExportMenu` component;
// it is re-exported here so consumers of this module (and the layout
// barrel) never need a second, drifting declaration. The export-menu
// *rendering* is delegated to `ExportMenu` too (with `visual="toolbar"`),
// so there is a single render path.
import { ExportMenu, type ExportMenuItem } from '../ExportMenu';
import { useTranslation } from 'react-i18next';
export type { ToggleOption, ToggleGroupConfig, ExportMenuItem };

export interface PrimaryActionMenuItem {
  label: string;
  onClick: () => void;
  'data-e2e'?: string;
}

interface TableOptionsProps {
  toggleOptions?: ToggleOption[];
  activeToggle?: string;
  onToggleChange?: (value: string) => void;
  secondaryToggle?: ToggleGroupConfig;
  /**
   * Accessible names for the filter pills. `ToggleGroup` renders a
   * `radiogroup`, which is announced as a bare group without one.
   *
   * The primary group falls back to a translated "Filter", which is honestly
   * what a lone pill row in a table toolbar is. The secondary has NO default
   * on purpose: a generic second name ("Additional filter") names the group
   * by its ordinal position in these props, tells a screen-reader user
   * nothing, and — being a real name — would satisfy axe forever. A toolbar
   * carrying two groups has to say what they mean, so pass both.
   *
   * Already-translated strings, please — these are user-facing.
   */
  toggleAriaLabel?: string;
  secondaryToggleAriaLabel?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /**
   * Immediate-search-on-Enter. `onSearchChange` already fires on
   * every keystroke, so consumers that debounce (`useSearchDebounce`) still
   * pay the debounce delay after the user presses Enter. Wire this to the
   * hook's `handleSubmit` to flush the debounce instead. Optional — when
   * omitted, Enter simply re-emits the current value.
   */
  onSearchSubmit?: (e: React.FormEvent) => void;
  showFilter?: boolean;
  onFilterClick?: () => void;
  primaryAction?: {
    label: string;
    onClick: () => void;
    /** Overrides the auto-generated `primary-action-btn-<label>` id. */
    'data-e2e'?: string;
  };
  /**
   * Renders a Chakra Menu in place of the primary-action button. Use when
   * the action has multiple variants (e.g. "Add SSO Config" → choose
   * SAML or SCIM). Mutually exclusive with `primaryAction`; if both are
   * provided, `primaryActionMenu` wins.
   */
  primaryActionMenu?: {
    label: string;
    items: PrimaryActionMenuItem[];
    'data-e2e'?: string;
  };
  exportMenu?: ExportMenuItem[];
  secondaryAction?: {
    label: string;
    onClick: () => void;
    'data-e2e'?: string;
  };
}

export function TableOptions({
  toggleOptions,
  activeToggle,
  onToggleChange,
  secondaryToggle,
  toggleAriaLabel,
  secondaryToggleAriaLabel,
  searchPlaceholder = 'common.searchPlaceholder',
  searchValue,
  onSearchChange,
  onSearchSubmit,
  showFilter = false,
  onFilterClick,
  primaryAction,
  primaryActionMenu,
  exportMenu,
  secondaryAction,
}: TableOptionsProps) {
  const hasToggles = (toggleOptions && toggleOptions.length > 0) || secondaryToggle;
  const searchId = useId();
  const { t } = useTranslation();

  const resolvedPlaceholder = useMemo(() => {
    const isGeneric =
      !searchPlaceholder ||
      searchPlaceholder === 'search' ||
      searchPlaceholder === 'searchPlaceholder' ||
      searchPlaceholder === 'common.searchPlaceholder' ||
      searchPlaceholder === 'search.placeholder';

    if (isGeneric) {
      const fb =
        t('common.searchPlaceholder', { defaultValue: '' }) ||
        t('searchPlaceholder', { defaultValue: '' });
      return typeof fb === 'string' && fb ? fb : 'Search...';
    }

    if (typeof searchPlaceholder === 'string' && searchPlaceholder.trim() !== '') {
      const val = t(searchPlaceholder, { defaultValue: searchPlaceholder });
      if (typeof val === 'string' && val) {
        return val;
      }
    }

    const fb =
      t('common.searchPlaceholder', { defaultValue: '' }) ||
      t('searchPlaceholder', { defaultValue: '' });
    return typeof fb === 'string' && fb ? fb : 'Search...';
  }, [t, searchPlaceholder]);

  // Same dual-key probe as the placeholder above: the two apps that ship
  // catalogs disagree on style (`common.filter` in sms-react, bare keys in
  // aim-react), and one-react has neither — so try both, then fall back to
  // English rather than rendering a raw key.
  const resolvedToggleLabel = useMemo(() => {
    if (toggleAriaLabel) return toggleAriaLabel;
    const fb =
      t('common.filter', { defaultValue: '' }) ||
      t('filter', { defaultValue: '' });
    return typeof fb === 'string' && fb ? fb : 'Filter';
  }, [t, toggleAriaLabel]);
  // The input is controlled, so the submitted value is simply the
  // `searchValue` prop — no `FormData` round-trip (a missed key lookup
  // would submit '' and clear the search).
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (onSearchSubmit) {
      onSearchSubmit(e);
      return;
    }
    onSearchChange?.(searchValue ?? '');
  };
  return (
    <Flex
      justify={hasToggles ? 'space-between' : 'flex-end'}
      align="center"
      gap={6}
      px={5}
      pt={3}
      pb={4}
    >
      {/* Left: Toggle groups */}
      {hasToggles && (
        <HStack gap={3} flex={1}>
          {toggleOptions && toggleOptions.length > 0 && onToggleChange && (
            <ToggleGroup
              options={toggleOptions}
              value={activeToggle || ''}
              onChange={onToggleChange}
              aria-label={resolvedToggleLabel}
            />
          )}
          {secondaryToggle && (
            <ToggleGroup
              options={secondaryToggle.options}
              value={secondaryToggle.value}
              onChange={secondaryToggle.onChange}
              aria-label={secondaryToggleAriaLabel}
            />
          )}
        </HStack>
      )}

      {/* Right: Search, Filter, Export, Action */}
      <HStack gap={3}>
        {/* Search — only rendered when the consumer can actually receive the
            value; without `onSearchChange` the box would be a read-only
            controlled input. */}
        {onSearchChange && (
          <form onSubmit={handleSearch}>
            <Field.Root>
              <Field.Label htmlFor={searchId} srOnly>
                {resolvedPlaceholder}
              </Field.Label>
              <HStack
                bg="surface.primary"
                borderWidth="1px"
                borderColor="border.line"
                borderRadius="8px"
                px={2.5}
                py={2}
                gap={2}
                w="190px"
              >
                <HiMagnifyingGlass size={20} color="text.primary" />
                <Input
                  id={searchId}
                  aria-label={resolvedPlaceholder}
                  placeholder={resolvedPlaceholder}
                  value={searchValue ?? ''}
                  onChange={(e) => onSearchChange(e.target.value)}
                  fontSize="14px"
                  color="text.primary"
                  border="none"
                  bg="surface.primary"
                  /* Wire identifier, never display copy — do not translate. */
                  name="search"
                  p={0}
                  h="auto"
                  _focusVisible={{
                    boxShadow: '0 0 0 1px var(--chakra-colors-border-action)',
                  }}
                  data-e2e="table-search-input"
                />
              </HStack>
            </Field.Root>
          </form>
        )}

        {/* Filter Button */}
        {showFilter && (
          <HStack
            as="button"
            bg="surface.primary"
            borderWidth="1px"
            borderColor="border.line"
            borderRadius="8px"
            px={2.5}
            py={2}
            gap={1}
            cursor="pointer"
            onClick={onFilterClick}
            _hover={{ bg: 'surface.secondary' }}
            color="text.primary"
            data-e2e="table-filter-button"
          >
            <HiFunnel size={20} color="text.primary" />
            <Text fontSize="14px" fontWeight="500" color="text.primary" px={1}>
              Filter
            </Text>
          </HStack>
        )}

        {/* Export Menu — delegated to the shared `ExportMenu` component.
            `visual="toolbar"` renders the bordered toolbar trigger; the
            single-item `e2eTitle` keeps the item-level `data-e2e` (falling
            back to "exportMenu") on the trigger. */}
        {exportMenu && exportMenu.length > 0 && (
          <ExportMenu
            items={exportMenu}
            visual="toolbar"
            e2eTitle={exportMenu.length === 1 ? exportMenu[0]['data-e2e'] : undefined}
          />
        )}

        {/* Secondary Action */}
        {secondaryAction && (
          <Button
            visual="secondary"
            onClick={secondaryAction.onClick}
            data-e2e={secondaryAction['data-e2e'] || `secondary-action-btn-${secondaryAction.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {secondaryAction.label}
          </Button>
        )}

        {/* Primary Action — Menu (preferred when provided) or Button */}
        {primaryActionMenu ? (
          <Menu.Root positioning={{ sameWidth: false }}>
            <Menu.Trigger asChild>
              <Button visual="primary" data-e2e={primaryActionMenu['data-e2e']}>
                <HiPlus size={18} />
                {primaryActionMenu.label}
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content
                  minWidth="200px"
                  padding="0px"
                  bg="surface.primary"
                  border="1px solid"
                  borderColor="border.secondary"
                  borderRadius="md"
                  overflow="hidden"
                >
                  {primaryActionMenu.items.map((item, index) => (
                    <Menu.Item
                      key={index}
                      value={item.label}
                      data-e2e={item['data-e2e']}
                      textTransform="capitalize"
                      fontSize="14px"
                      fontWeight={600}
                      bg="surface.primary"
                      color="text.primary"
                      border="none"
                      borderRadius="none"
                      onClick={item.onClick}
                      _hover={{
                        bg: 'surface.secondary',
                        cursor: 'pointer',
                      }}
                      _focus={{ bg: 'surface.secondary' }}
                      _focusVisible={{
                        outline: '2px solid',
                        outlineColor: 'border.action',
                        outlineOffset: '-2px',
                      }}
                      py="10px"
                      px="16px"
                    >
                      <Text>{item.label}</Text>
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        ) : primaryAction ? (
          <Button visual="primary" onClick={primaryAction.onClick} data-e2e={primaryAction['data-e2e'] || `primary-action-btn-${primaryAction.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <HiPlus size={18} />
            {primaryAction.label}
          </Button>
        ) : null}
      </HStack>
    </Flex>
  );
}
